<template>
    <div>
      <AdminHeader />
  
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <button type="button" @click="$router.go(-1)" class="btn-back">
            <font-awesome-icon icon="arrow-left" />
          </button>
          <h5 class="page-title mt-2">New Invoice</h5>
          <p class="page-sub">Fill in the details below to create a customer invoice</p>
        </div>
      </div>
  
      <!-- Validation Errors -->
      <div v-if="validationErrors?.length" class="error-banner">
        <font-awesome-icon icon="exclamation-circle" class="error-icon" />
        <ul class="mb-0 ps-1">
          <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
        </ul>
      </div>
  
      <!-- Loading Skeleton -->
      <div v-if="loading" class="form-card mb-4">
        <div class="skeleton-grid"><div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div></div>
      </div>
  
      <form v-else @submit.prevent>
  
        <!-- ── Invoice Meta ── -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--blue"><font-awesome-icon icon="file-invoice" /></div>
            <div><p class="section-title">Invoice Details</p><p class="section-sub">Basic invoice information</p></div>
          </div>
          <div class="fields-grid">
  
            <div class="field-group">
              <label class="field-label">Invoice Number <span class="req">*</span></label>
              <input type="text" class="field-input" v-model="invoice.invoice_no" placeholder="INV-000001" />
            </div>
  
            <div class="field-group">
              <label class="field-label">Invoice Date <span class="req">*</span></label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker v-model="invoice.invoice_date" :enableTimePicker="false" class="clean-datepicker" />
              </div>
            </div>
  
            <div class="field-group">
              <label class="field-label">Due Date <span class="req">*</span></label>
              <div class="datepicker-wrap">
                <font-awesome-icon icon="calendar" class="datepicker-icon" />
                <Datepicker v-model="invoice.due_date" :enableTimePicker="false" class="clean-datepicker" />
              </div>
            </div>
  
            <div class="field-group">
              <label class="field-label">Customer <span class="req">*</span></label>
              <Multiselect v-model="invoice.customer_id" label="name" :options="customer_arr"
                placeholder="Select customer" :searchable="true" :close-on-select="true" class="custom-multiselect" />
            </div>
  
            <div class="field-group">
              <label class="field-label">Currency <span class="req">*</span></label>
              <Multiselect v-model="invoice.currency_id" label="name" :options="currency_arr"
                placeholder="Select currency" :searchable="true" :close-on-select="true"
                class="custom-multiselect" @change="getSelectedCurrency($event)" />
            </div>
  
            <div class="field-group">
              <label class="field-label">Bank</label>
              <Multiselect v-model="invoice.bank_id" label="name" :options="bank_arr"
                placeholder="Select bank" :searchable="true" :close-on-select="true" class="custom-multiselect" />
            </div>
  
            <div class="field-group">
              <label class="field-label">Tax</label>
              <Multiselect v-model="invoice.tax_id" label="name" :options="taxes"
                placeholder="Select tax" :searchable="true" :close-on-select="true"
                class="custom-multiselect" @change="getSelectedTax($event)" />
            </div>
  
          </div>
        </div>
  
        <!-- ── Line Items ── -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--teal"><font-awesome-icon icon="list" /></div>
            <div><p class="section-title">Invoice Items</p><p class="section-sub">Add products or services</p></div>
            <button type="button" class="btn-outline ms-auto" @click="addNewItem">
              <font-awesome-icon icon="plus" /> Add Item
            </button>
          </div>
  
          <div class="table-wrap">
            <table class="items-table">
              <thead>
                <tr>
                  <th style="width:40%">Description</th>
                  <th style="width:10%">Qty</th>
                  <th style="width:18%">Price ({{ currency }})</th>
                  <th style="width:8%;text-align:center">Tax</th>
                  <th style="width:18%;text-align:right">Total ({{ currency }})</th>
                  <th style="width:6%;text-align:center"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, i) in invoice.products" :key="i" class="item-row">
                  <td>
                    <input type="text" class="field-input item-input" v-model="item.description" placeholder="Item description" />
                  </td>
                  <td>
                    <input type="number" class="field-input item-input text-center" min="1" v-model="item.quantity" @input="getGrandTotal(); getTotalTax()" />
                  </td>
                  <td>
                    <input type="text" class="field-input item-input text-right" v-model="item.price" @input="getGrandTotal(); getTotalTax()" />
                  </td>
                  <td style="text-align:center">
                    <label class="toggle toggle--sm">
                      <input type="checkbox" value="1" v-model="item.apply_tax" @change="getTotalTax()" />
                      <span class="toggle-slider"></span>
                    </label>
                  </td>
                  <td style="text-align:right">
                    <span class="item-total">{{ formatPrice(getItemTotal(item)) }}</span>
                  </td>
                  <td style="text-align:center">
                    <button type="button" class="action-btn action-btn--delete" @click="deleteItem(i)" :disabled="invoice.products.length == 1" title="Remove">
                      <font-awesome-icon icon="trash" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
  
          <!-- Totals Summary -->
          <div class="totals-block">
            <div class="totals-inner">
              <div class="totals-row">
                <span class="totals-label">Subtotal</span>
                <span class="totals-value">{{ currency }}{{ formatPrice(invoice.total_amount) }}</span>
              </div>
              <div class="totals-row" v-if="invoice.discount_amount > 0">
                <span class="totals-label">
                  Discount {{ invoice.discount_type === 'percentage' ? `(${invoice.discount}%)` : '' }}
                </span>
                <span class="totals-value deduction-value">− {{ currency }}{{ formatPrice(invoice.discount_amount) }}</span>
              </div>
              <div class="totals-row" v-if="invoice.total_tax > 0">
                <span class="totals-label">Tax ({{ tax.tax_name }})</span>
                <span class="totals-value">{{ currency }}{{ formatPrice(invoice.total_tax) }}</span>
              </div>
              <div class="totals-divider"></div>
              <div class="totals-row totals-row--grand">
                <span class="totals-label-grand">Total</span>
                <span class="totals-value-grand">{{ currency }}{{ formatPrice(grandTotal) }}</span>
              </div>
            </div>
  
            <div class="discount-link" v-if="invoice.total_amount > 0">
              <button v-if="!invoice.discount_amount" type="button" class="discount-btn" @click="openDiscountModal">
                <font-awesome-icon icon="tag" class="me-1" /> Apply Discount
              </button>
              <button v-else type="button" class="discount-btn discount-btn--remove" @click="removeDiscount">
                <font-awesome-icon icon="times" class="me-1" /> Remove Discount
              </button>
            </div>
          </div>
        </div>
  
        <!-- ── Notes & Options ── -->
        <div class="form-card mb-4">
          <div class="section-header">
            <div class="section-icon section-icon--orange"><font-awesome-icon icon="sticky-note" /></div>
            <div><p class="section-title">Notes & Options</p><p class="section-sub">Additional settings for this invoice</p></div>
          </div>
  
          <div class="field-group mb-4">
            <label class="field-label">Notes</label>
            <textarea class="field-input" rows="3" v-model="invoice.note" placeholder="Thanks for your business"></textarea>
          </div>
  
          <!-- Recurring toggle -->
          <div class="recurring-toggle-wrap">
            <div class="recurring-toggle-label">
              <div>
                <p class="field-label mb-0">Make Recurring</p>
                <p class="field-hint">Bill your customers automatically on a schedule</p>
              </div>
              <label class="toggle ms-auto">
                <input type="checkbox" :true-value="1" :false-value="null" v-model="invoice.is_reocurring" />
                <span class="toggle-slider"></span>
              </label>
            </div>
          </div>
  
          <div v-if="invoice.is_reocurring == 1" class="fields-grid mt-3">
            <div class="field-group">
              <label class="field-label">Frequency <span class="req">*</span></label>
              <select class="field-input field-select" v-model="invoice.reocurring_type">
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="half-year">Half Year</option>
                <option value="yearly">Yearly</option>
              </select>
            </div>
            <div class="field-group">
              <label class="field-label">Next Issue Date <span class="req">*</span></label>
              <input type="date" class="field-input" v-model="invoice.next_invoice_date" />
            </div>
            <div class="field-group">
              <label class="field-label">{{ recurringCountLabel }} <span class="req">*</span></label>
              <input type="number" class="field-input" v-model="invoice.reocurring_count" min="1" />
            </div>
          </div>
        </div>
  
        <!-- Footer -->
        <div class="form-footer">
          <button type="button" @click="$router.go(-1)" class="btn-outline">Cancel</button>
          <button type="button" class="btn-primary-action" :disabled="crudLoading" @click="openConfirmModal">
            <font-awesome-icon icon="file-invoice" class="me-2" />
            Review & Create Invoice
          </button>
        </div>
  
      </form>
  
  
      <!-- ── Discount Modal ── -->
      <div class="modal fade" id="discountModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-header-clean">
              <div class="modal-title-icon" style="background:rgba(124,58,237,.1);color:#7c3aed;">
                <font-awesome-icon icon="tag" />
              </div>
              <div><h6 class="modal-clean-title">Apply Discount</h6><p class="modal-clean-sub">Reduce the invoice total</p></div>
              <button type="button" class="btn-close ms-auto" @click="closeDiscountModal"></button>
            </div>
            <form @submit.prevent="applyDiscount">
              <div class="modal-body">
                <div class="modal-fields">
                  <div class="field-group">
                    <label class="field-label">Discount Type <span class="req">*</span></label>
                    <select class="field-input field-select" v-model="invoice.discount_type">
                      <option value="" disabled>— Select type —</option>
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  <div class="field-group" v-if="invoice.discount_type">
                    <label class="field-label">{{ invoice.discount_type === 'percentage' ? 'Discount %' : 'Discount Amount' }}</label>
                    <input type="number" class="field-input" v-model="invoice.discount" min="0"
                      :placeholder="invoice.discount_type === 'percentage' ? '10' : '500'" />
                  </div>
                </div>
              </div>
              <div class="modal-footer border-0 pt-0">
                <button type="button" class="btn-outline" @click="closeDiscountModal">Cancel</button>
                <button type="submit" class="btn-primary-action">Apply Discount</button>
              </div>
            </form>
          </div>
        </div>
      </div>
  
  
      <!-- ── Confirm Create Modal ── -->
      <div class="modal fade" id="confirm_modal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-sm">
          <div class="modal-content modal-clean">
            <div class="modal-icon-wrap">
              <div class="modal-icon modal-icon--blue"><font-awesome-icon icon="file-invoice" /></div>
            </div>
            <div class="modal-body-clean">
              <p class="modal-heading">Create Invoice?</p>
              <p class="modal-subtext">
                Confirm creation of invoice <strong>#{{ invoice.invoice_no }}</strong> for a total of
                <strong>{{ currency }}{{ formatPrice(grandTotal) }}</strong>.
              </p>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-btn modal-btn--cancel" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="modal-btn modal-btn--blue" :disabled="crudLoading" @click="createData">
                <span v-if="crudLoading" class="spinner-border spinner-border-sm me-1" role="status"></span>
                <font-awesome-icon v-else icon="check" class="me-1" />
                {{ crudLoading ? 'Creating...' : 'Yes, Create' }}
              </button>
            </div>
          </div>
        </div>
      </div>
  
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import Multiselect from '@vueform/multiselect';
  import { useToast } from 'vue-toastification';
  import { useRouter } from 'vue-router';
  import AdminHeader from './component/header.vue';
  
  export default {
    components: { AdminHeader, Multiselect },
  
    data() {
      return {
        toast:            useToast(),
        router:           useRouter(),
        validationErrors: [],
        customer_arr:     [],
        currency_arr:     [],
        bank_arr:         [],
        taxes:            [],
        currency:         '',
        tax:              '',
        loading:          false,
        crudLoading:      false,
  
        invoice: {
          invoice_date:      new Date(),
          invoice_no:        `INV-${Math.floor(100000 + Math.random() * 900000)}`,
          customer_id:       '',
          currency_id:       '',
          bank_id:           '',
          tax_id:            '',
          due_date:          new Date(),
          note:              'Thanks for your business',
          total_tax:         0,
          total_amount:      0,
          is_reocurring:     null,
          reocurring_type:   'monthly',
          next_invoice_date: null,
          reocurring_count:  0,
          discount_type:     '',
          discount:          '',
          discount_amount:   0,
          products: [{ description:'', quantity:1, price:0, apply_tax:'', tax:'' }],
        },
      };
    },
  
    computed: {
      grandTotal() {
        return (this.invoice.total_amount + this.invoice.total_tax) - this.invoice.discount_amount;
      },
      recurringCountLabel() {
        const map = { daily:'How Many Days?', weekly:'How Many Weeks?', monthly:'How Many Months?', quarterly:'How Many Quarters?', 'half-year':'How Many Times?', yearly:'How Many Years?' };
        return map[this.invoice.reocurring_type] ?? 'Count';
      },
    },
  
    created() {
      Promise.all([
        this.getActiveCurrency(),
        this.fetchClients(),
        this.fetchCurrency(),
        this.fetchBanks(),
        this.fetchTaxes(),
      ]);
    },
  
    methods: {
      async fetchClients() {
        axios.get('/settings/customers/without-default').then(response => {
          this.customer_arr = (response?.data?.data ?? []).map(c => ({ value: c.id, name: c.customer_name }));
        }).catch(() => {});
      },
      async fetchCurrency() {
        axios.get('/settings/currency').then(response => {
          this.currency_arr = (response?.data?.data?.data ?? []).map(c => ({ value: c.id, name: `${c.name} - ${c.symbol}` }));
        }).catch(() => {});
      },
      async getActiveCurrency() {
        axios.get('/settings/currency/active').then(response => {
          this.currency = response?.data?.data?.symbol ?? '';
        }).catch(() => {});
      },
      async getSelectedCurrency(id) {
        if (!id) return;
        axios.get(`/settings/currency/${id}`).then(response => {
          this.currency = response?.data?.data?.symbol ?? '';
        }).catch(() => {});
      },
      async fetchBanks() {
        axios.get('/settings/bank/all/active').then(response => {
          this.bank_arr = (response?.data?.data ?? []).map(b => ({ value: b.id, name: b.bank_name }));
        }).catch(() => {});
      },
      async fetchTaxes() {
        axios.get('/settings/tax').then(response => {
          this.taxes = (response?.data?.data ?? []).map(t => ({ value: t.id, name: t.tax_name }));
        }).catch(() => {});
      },
      async getSelectedTax(id) {
        if (!id) return;
        axios.get(`/settings/tax/${id}`).then(response => {
          this.tax = response?.data?.data;
          this.getTotalTax();
        }).catch(() => {});
      },
  
      getItemTotal(item) {
        return (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);
      },
  
      getGrandTotal() {
        this.invoice.total_amount = this.invoice.products.reduce((sum, item) => sum + this.getItemTotal(item), 0);
      },
  
      getTotalTax() {
        const pct = this.tax?.tax_percentage ?? 0;
        this.invoice.total_tax = this.invoice.products.reduce((sum, item) => {
          if (!item.apply_tax) return sum;
          return sum + (pct / 100) * this.getItemTotal(item);
        }, 0);
      },
  
      addNewItem() {
        this.invoice.products.push({ description:'', quantity:1, price:0, apply_tax:'', tax:'' });
      },
  
      deleteItem(i) {
        this.invoice.products.splice(i, 1);
        this.getGrandTotal(); this.getTotalTax();
      },
  
      applyDiscount() {
        this.invoice.discount_amount = this.invoice.discount_type === 'percentage'
          ? (this.invoice.discount / 100) * this.invoice.total_amount
          : parseFloat(this.invoice.discount) || 0;
        this.closeDiscountModal();
      },
  
      removeDiscount() {
        this.invoice.discount_type = ''; this.invoice.discount = ''; this.invoice.discount_amount = 0;
      },
  
      openConfirmModal()  { window.$('#confirm_modal').modal('show'); },
      openDiscountModal() { window.$('#discountModal').modal('show'); },
      closeDiscountModal() { window.$('#discountModal').modal('hide'); },
  
      async createData() {
        this.crudLoading = true;
        axios.post('invoice/create', this.invoice)
          .then(response => {
            this.crudLoading = false;
            if (response.data.status === 200) {
              this.toast.success(response.data.message);
              this.$router.push({ path: '/invoice-history' });
            } else { this.toast.error(response.data.message); }
          })
          .catch(error => {
            this.crudLoading = false;
            this.toast.error(error.response?.data?.message ?? 'Something went wrong');
          });
      },
  
      formatPrice(value) {
        if (!value && value !== 0) return '0.00';
        return Number(value).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 });
      },
    },
  };
  </script>
  
  <style src="@vueform/multiselect/themes/default.css"></style>
  
  <style scoped>
  .page-header { margin-bottom:20px; }
  .page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub    { font-size:13px; color:#7a90b0; margin:0; }
  .btn-back    { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; }
  .btn-back:hover { color:#1a2b45; }
  
  .error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
  .error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }
  
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
  .mb-4      { margin-bottom:16px; }
  .mt-3      { margin-top:12px; }
  .mb-0      { margin-bottom:0; }
  
  .skeleton-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton     { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  
  .section-header { display:flex; align-items:center; gap:14px; margin-bottom:20px; flex-wrap:wrap; }
  .section-icon   { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
  .section-icon--blue   { background:rgba(0,74,173,.1);   color:#004AAD; }
  .section-icon--teal   { background:rgba(13,148,136,.1); color:#0d9488; }
  .section-icon--orange { background:rgba(224,140,0,.1);  color:#e08c00; }
  .section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .section-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .ms-auto { margin-left:auto; }
  .me-1 { margin-right:4px; } .me-2 { margin-right:8px; }
  
  .fields-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; }
  .field-group { display:flex; flex-direction:column; gap:6px; }
  .field-label { font-size:13px; font-weight:600; color:#1a2b45; }
  .field-hint  { font-size:12px; color:#7a90b0; margin:0; }
  .req         { color:#e03535; }
  .field-input { padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13.5px; color:#1a2b45; background:#fff; outline:none; width:100%; transition:border-color .15s; font-family:inherit; resize:vertical; }
  .field-input:focus { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .field-select { appearance:auto; cursor:pointer; }
  :deep(.custom-multiselect) { --ms-border-color:#dde5f0; --ms-border-radius:8px; --ms-font-size:13.5px; }
  :deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
  
  .datepicker-wrap { position:relative; }
  .datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
  :deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
  :deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
  
  /* ── Items Table ── */
  .table-wrap   { overflow-x:auto; margin-bottom:0; }
  .items-table  { width:100%; border-collapse:collapse; min-width:600px; }
  .items-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 12px; border-bottom:2px solid #e4eaf2; white-space:nowrap; }
  .items-table tbody .item-row { border-bottom:1px solid #f0f4f8; }
  .items-table tbody .item-row:last-child { border-bottom:none; }
  .items-table tbody td { padding:10px 8px; vertical-align:middle; }
  .item-input   { font-size:13.5px; padding:7px 10px; }
  .text-center  { text-align:center; }
  .text-right   { text-align:right; }
  .item-total   { font-size:14px; font-weight:700; color:#1a2b45; display:block; text-align:right; padding-right:4px; }
  .action-btn   { width:28px; height:28px; border-radius:7px; border:1px solid #e4eaf2; display:flex; align-items:center; justify-content:center; font-size:11px; cursor:pointer; background:#fff; margin:auto; }
  .action-btn--delete { color:#e03535; } .action-btn--delete:hover:not(:disabled) { background:#fff0f0; border-color:#fac5c5; }
  .action-btn:disabled { opacity:.4; cursor:not-allowed; }
  
  /* ── Totals ── */
  .totals-block  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:16px; padding:20px 0 0; border-top:2px solid #e4eaf2; margin-top:16px; }
  .totals-inner  { display:flex; flex-direction:column; gap:8px; min-width:260px; }
  .totals-row    { display:flex; justify-content:space-between; align-items:center; gap:40px; font-size:13.5px; }
  .totals-label  { color:#4a5e78; }
  .totals-value  { font-weight:600; color:#1a2b45; }
  .totals-divider { height:1px; background:#e4eaf2; margin:4px 0; }
  .totals-row--grand { padding-top:4px; }
  .totals-label-grand { font-size:15px; font-weight:700; color:#1a2b45; }
  .totals-value-grand { font-size:20px; font-weight:800; color:#004AAD; }
  .deduction-value { color:#e03535; }
  
  .discount-link { display:flex; align-items:flex-end; padding-bottom:4px; }
  .discount-btn  { background:transparent; border:none; font-size:13px; font-weight:600; color:#7c3aed; cursor:pointer; display:flex; align-items:center; padding:0; }
  .discount-btn:hover { color:#6d28d9; }
  .discount-btn--remove { color:#e03535; }
  .discount-btn--remove:hover { color:#c42b2b; }
  
  /* ── Recurring ── */
  .recurring-toggle-wrap { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:16px; }
  .recurring-toggle-label { display:flex; align-items:center; }
  
  /* ── Toggle ── */
  .toggle     { position:relative; display:inline-block; width:36px; height:20px; flex-shrink:0; }
  .toggle--sm { width:30px; height:16px; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; cursor:pointer; inset:0; background:#dde5f0; border-radius:20px; transition:background .2s; }
  .toggle-slider::before { content:''; position:absolute; width:14px; height:14px; left:3px; bottom:3px; background:#fff; border-radius:50%; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.15); }
  .toggle--sm .toggle-slider::before { width:10px; height:10px; }
  .toggle input:checked + .toggle-slider { background:#004AAD; }
  .toggle input:checked + .toggle-slider::before { transform:translateX(16px); }
  .toggle--sm input:checked + .toggle-slider::before { transform:translateX(14px); }
  
  /* ── Footer ── */
  .form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; margin-top:4px; }
  .btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:10px 24px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; gap:7px; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-outline:hover { background:#f4f7fb; }
  .btn-outline::after { display:none !important; }
  
  /* ── Modals ── */
  .modal-clean { border:none; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.14); overflow:hidden; }
  .modal-header-clean { display:flex; align-items:flex-start; gap:14px; padding:20px 24px 16px; border-bottom:1px solid #f0f4f8; }
  .modal-title-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
  .modal-clean-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
  .modal-clean-sub   { font-size:12px; color:#7a90b0; margin:0; }
  .modal-fields { display:flex; flex-direction:column; gap:13px; }
  .modal-icon-wrap { display:flex; justify-content:center; padding:28px 24px 0; }
  .modal-icon { width:60px; height:60px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:26px; }
  .modal-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
  .modal-body-clean { padding:16px 28px 8px; text-align:center; }
  .modal-heading    { font-size:17px; font-weight:700; color:#1a2b45; margin:0 0 8px; }
  .modal-subtext    { font-size:13.5px; color:#7a90b0; margin:0; line-height:1.6; }
  .modal-actions    { display:flex; gap:10px; padding:18px 24px 24px; justify-content:center; }
  .modal-btn        { display:inline-flex; align-items:center; justify-content:center; font-size:13px; font-weight:600; padding:9px 22px; border-radius:8px; border:none; cursor:pointer; flex:1; }
  .modal-btn--cancel { background:#f4f7fb; color:#1a2b45; border:1px solid #dde5f0; }
  .modal-btn--blue   { background:#004AAD; color:#fff; } .modal-btn--blue:hover { background:#003b8e; }
  .modal-btn:disabled { opacity:.6; cursor:not-allowed; }
  
  @media(max-width:991px) { .fields-grid { grid-template-columns:repeat(2,1fr); } .skeleton-grid { grid-template-columns:repeat(2,1fr); } }
  @media(max-width:576px) { .fields-grid,.skeleton-grid { grid-template-columns:1fr; } .form-card { padding:16px; } .form-footer { flex-direction:column; } .btn-primary-action,.btn-outline { width:100%; justify-content:center; } }
  </style>